import React, { useRef, useState, useContext, useEffect } from 'react'

import { userContext } from 'global/context/sub-contexts/userContext'

import Slider from 'react-slick'
import ServerError from 'steps/shared/ServerError'

import 'slick-carousel/slick/slick.css'
import 'slick-carousel/slick/slick-theme.css'

import ArrowButton from 'images/inline/arrow-right.svg'

import StartSlide from 'steps/StartSlide'
import backgroundStyle from 'steps/shared/quiz-trivia/backgroundStyle'
import QuestionSlide from './QuestionSlide'
import ResultSlide from './ResultSlide'

import {
  scrollToTopOfCurrentStep,
  scrollToTopOfEditorialStep,
} from 'services/break'
import {
  start as serviceStart,
  complete as serviceComplete,
  getTextTriviaAnswerResult,
  getTextTriviaFinalResult,
} from 'services/texttrivia'
import analytics from 'global/AnalyticsData'

import './text-trivia.scss'
import 'steps/shared/quiz-trivia/editorial-trivia-quiz.scss'

const TextTriviaStep = ({
  breakId,
  step,
  onGoToNext,
  onStepStart,
  onStepComplete,
  onStepSkip,
  isEmbed,
  isEditorial = false,
  relatedBreaks,
  unlockNextStep,
  scrollComplete,
  HtmlFooter,
  skipButton,
  nextStepButton,
}) => {
  const trivia = step.textTriviaSet
  const [index, setIndex] = useState(0)
  const [score, setScore] = useState([])
  const [quizResult, setQuizResult] = useState(null)
  const [stepState, setStepState] = useState('NOT_STARTED') // NOT_STARTED, IN_PROGRESS, COMPLETE

  const [isQuizDone, setIsQuizDone] = useState(false) // person has answered every question

  const [answeredText, setAnsweredText] = useState(null)
  const [answerResultSlide, showAnswerResultSlide] = useState(false)
  const [selectedAnswerId, setSelectedAnswerId] = useState(null)
  const [currentQuestionId, setCurrentQuestionId] = useState(null)
  const [currentAnswerIsTrue, setCurrentAnswerIsTrue] = useState(false)
  const [serverError, setIsServerError] = useState(false)
  const [isGuest, setIsGuest] = useState(false)
  const [guestPointsEarned, setGuestPointsEarned] = useState(null)
  //TODO - consider putting this into a more global context to reduce code redundancy?
  const [waitingForServer, setWaitingForServer] = useState(false)

  const user_context = useContext(userContext)
  const askToLogin = user_context.askToLogin

  let nextArrowEl = useRef(null)
  const setArrowLocked = () => {
    if (nextArrowEl && nextArrowEl.current) {
      nextArrowEl.current.disabled = true
    }
  }

  var settings = {
    dots: false,
    arrows: false,
    swipe: false,
    infinite: false,
    speed: 500,
    slidesToShow: 1,
    adaptiveHeight: true,
    slidesToScroll: 1,
    touchMove: false,
    //TODO - consider refactoring with handlenextclick
    beforeChange: function (e, currentSlide) {
      setIndex(currentSlide)
      showAnswerResultSlide(false)
    },
  }

  const sliderRef = useRef(null)

  const checkIfUnauthorizedError = e => {
    if (isEditorial !== true) {
      if (e.message === '401') {
        user_context.unauthorized()
        askToLogin()
      } else {
        setIsServerError(true)
      }
    }
  }

  const handleNextClick = async () => {
    //Checks quiz/trivia answer.
    const checkAnswer = async () => {
      //check answer here
      try {
        const response = await getTextTriviaAnswerResult(
          trivia.id,
          currentQuestionId,
          selectedAnswerId,
        )
        if (response.detail.correct) {
          setCurrentAnswerIsTrue(true)
          setScore(score.concat(1))
        } else {
          setCurrentAnswerIsTrue(false)
          setScore(score.concat(0))
        }
        setWaitingForServer(false)
        showAnswerResultSlide(true)
      } catch (e) {
        console.warn('Trivia CheckAnswer', e)
        setWaitingForServer(false)
        checkIfUnauthorizedError(e)
      }
    }

    //Function that runs after successful start of step/break.
    const afterServiceStart = async result => {
      if (onStepStart) {
        onStepStart(step, breakId, result.detail.attempt)
      }
      setStepState('IN_PROGRESS')
      if (isEditorial === true) {
        await checkAnswer()
      } else {
        setWaitingForServer(false)
        sliderRef.current.slickNext()
      }
    }

    stepState !== 'COMPLETE' &&
      setTimeout(() => {
        if (isEditorial) {
          scrollToTopOfEditorialStep()
        } else {
          scrollToTopOfCurrentStep(step.id)
        }
      }, 300)

    setWaitingForServer(true)
    try {
      // const profileInfo = await getProfileInfo()
      // console.log('points: ', profileInfo.detail.user.points)

      if (stepState === 'NOT_STARTED') {
        serviceStart(breakId, step.id)
          .then(result => {
            afterServiceStart(result)
          })
          .catch(e => {
            console.warn('Trivia end', e)
            setWaitingForServer(false)
            checkIfUnauthorizedError(e)
            return false
          })
      }

      if (stepState === 'IN_PROGRESS') {
        //If we're in the thick of it, answering questions, then let's return an answer
        if (!isQuizDone && !answerResultSlide) {
          checkAnswer()
        }

        //TODO - consider refactor here
        else if (!isQuizDone && answerResultSlide) {
          //if final slide, set to complete and get final results

          let finalAmount = trivia.questions.length
          if (isEditorial === true) {
            finalAmount = trivia.questions.length - 1
          }
          if (index === finalAmount) {
            const response = await serviceComplete(breakId, step.id)
            setWaitingForServer(false)

            const { totalPoints, awardedPoints } = response.detail
            user_context.setPoints(totalPoints)
            setGuestPointsEarned(awardedPoints)
            const totalScore = score.reduce(
              (accumulator, currentScore) => accumulator + currentScore,
            )
            const result = trivia.results.find(
              result =>
                result.minCorrect <= totalScore &&
                result.maxCorrect >= totalScore,
            )
            console.log(result)
            getTextTriviaFinalResult(trivia.id, result.id)
            setQuizResult(result)

            window.dataLayer.push({
              event: 'step_interaction_trivia_result',
              break: {
                result_id: result.id,
                result: result.title,
                context_id: trivia.id,
                context: trivia.headline,
              },
            })

            analytics.stepEvent('step_interaction_trivia_result', step, {
              result_id: result.id,
              result: result.title,
              context_id: trivia.id,
              context: trivia.headline,
            })

            setIsQuizDone(true)
            setStepState('COMPLETE')
            unlockNextStep(step.id)
          } else {
            setWaitingForServer(false)
          }
          //move on to next slide.
          sliderRef.current.slickNext()
          setWaitingForServer(false)
        }
      }

      if (stepState === 'COMPLETE') {
        setWaitingForServer(false)
        handleCompleteClick()
      }
    } catch (e) {
      console.warn('Trivia handlenextclick', e)
      setWaitingForServer(false)
      checkIfUnauthorizedError(e)
    }
  }

  const handleAnswerClick = ({
    question,
    answer,
    answeredText,
    currentPosition,
    totalQuestions,
  }) => {
    setAnsweredText(answeredText)
    setSelectedAnswerId(answer.id)
    setCurrentQuestionId(question.id)

    window.dataLayer.push({
      event: 'step_interaction_trivia_answer_selected',
      break: {
        context_id: question.id,
        context: question.question,
        result_id: answer.id,
        result: answer.text,
        context_total: totalQuestions,
        context_position: currentPosition,
      },
    })

    analytics.stepEvent('step_interaction_trivia_answer_selected', step, {
      context_id: question.id,
      context: question.question,
      result_id: answer.id,
      result: answer.text,
      context_total: totalQuestions,
      context_position: currentPosition,
    })
  }

  const handleCompleteClick = async () => {
    const response = await serviceComplete(breakId, step.id)
    setWaitingForServer(false)
    console.log('Trivia Complete')
    const { totalPoints } = response.detail
    user_context.setPoints(totalPoints)
    onStepComplete && onStepComplete(step.id)
    onGoToNext && onGoToNext(step.id)
    setArrowLocked()
  }

  useEffect(() => {
    if (currentQuestionId && selectedAnswerId) {
      // to ensure yellow selected answer dot appears
      setTimeout(() => {
        handleNextClick()
      }, 100)
    }
  }, [currentQuestionId, selectedAnswerId])

  useEffect(() => {
    if (!step.hasStartScreen) {
      handleNextClick()
    }
  }, [])

  // listen for scrollComplete and fire off handleCompleteClick when toggled
  useEffect(() => {
    if (scrollComplete) {
      handleCompleteClick()
    }
  }, [scrollComplete])

  return (
    <div className="sponsored-flow-panel " style={backgroundStyle(step)}>
      <section
        className={`text-trivia-step waiting-${waitingForServer} ${
          isEditorial ? 'text-trivia-editorial-break' : ''
        } ${answerResultSlide ? 'is-displaying-answer' : ''}`}
      >
        <Slider
          ref={slider => (sliderRef.current = slider)}
          {...settings}
          className="trivia__slider"
        >
          {/* Editorial breaks have headers to show "start screen" content instead. */}
          {isEditorial !== true && (
            <StartSlide
              stepType={step.type}
              headline={step.startHeader}
              para={step.startBody}
              footer={step.startFooter}
              backgroundImageId={step.startBackgroundImageId}
              backgroundColor={step.startBackgroundColor}
              onStartClicked={handleNextClick}
            />
          )}
          {trivia.questions.map((item, i, arr) => {
            return (
              <QuestionSlide
                skipButton={skipButton}
                step={step}
                question={item}
                currentPosition={i + 1}
                totalQuestions={arr.length}
                currentAnswerIsTrue={currentAnswerIsTrue}
                key={i}
                handleAnswerClick={handleAnswerClick}
                backgroundImage={step.backgroundImage}
                backgroundColor={step.backgroundColor}
                selectedAnswerId={selectedAnswerId}
                answeredText={answeredText}
                setSelectedAnswerId={setSelectedAnswerId}
                answerResultSlide={answerResultSlide}
                isEditorial={isEditorial}
                nextButtonHandler={handleNextClick}
                nextStepButton={nextStepButton}
              />
            )
          })}

          <ResultSlide
            isEditorial={isEditorial}
            isGuest={isGuest}
            result={quizResult}
            currentSlide={trivia.questions.length}
            questionCount={trivia.questions.length}
            backgroundImage={step.backgroundImage}
            guestPointsEarned={guestPointsEarned}
            relatedBreaks={relatedBreaks}
            socialShareOn={step.socialShareOn || false}
          />
        </Slider>
        {isEditorial !== true && (
          <>
            {(answerResultSlide || isQuizDone) && (
              <button
                ref={nextArrowEl}
                className={`quiz-button${isQuizDone ? ' done' : ''}`}
                id="next-btn"
                onClick={handleNextClick}
                aria-label="Next"
              >
                <ArrowButton />
              </button>
            )}
          </>
        )}
        {HtmlFooter && <HtmlFooter />}
      </section>
      {serverError && <ServerError />}
    </div>
  )
}

export default TextTriviaStep
